import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing } from 'lucide-react';

import { tenantConfigApi, type AlertRule } from '@/features/tenant-config/api';

const KNOWN_ALERTS: { type: string; label: string }[] = [
  { type: 'GPS_DISABLED', label: 'GPS disabled' },
  { type: 'PROLONGED_IDLE', label: 'Prolonged idle' },
  { type: 'AFTER_HOURS_MOVEMENT', label: 'After-hours movement' },
  { type: 'SPEED_EXCEEDED', label: 'Speed exceeded' },
  { type: 'GEOFENCE_VIOLATION', label: 'Geofence violation' },
  { type: 'WORK_UNIT_DURATION_ANOMALY', label: 'Work-unit duration anomaly' },
  { type: 'FUEL_ANOMALY', label: 'Fuel anomaly' },
  { type: 'DOCUMENT_EXPIRED', label: 'Document expired' },
  { type: 'DOCUMENT_EXPIRING', label: 'Document expiring' },
  { type: 'MAINTENANCE_OVERDUE', label: 'Maintenance overdue' },
  { type: 'NO_CHECK_IN', label: 'No check-in (safety)' },
];

export function AlertRulesPage() {
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: tenantConfigApi.listAlertRules,
  });
  const [draft, setDraft] = useState<AlertRule | null>(null);

  const save = useMutation({
    mutationFn: (rule: AlertRule) => tenantConfigApi.upsertAlertRule(rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setDraft(null);
    },
  });

  const byType = new Map<string, AlertRule>((rules.data ?? []).map((r) => [r.alert_type, r]));

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <header>
        <h1 className="page-title flex items-center gap-2.5">
          <BellRing className="h-6 w-6 text-primary" />
          Alert rules
        </h1>
        <p className="page-subtitle">
          Enable/disable individual alert types and tune their thresholds.
        </p>
      </header>
      <section className="card !p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Alert</th>
              <th className="px-4 py-3">Enabled</th>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Thresholds</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {KNOWN_ALERTS.map((meta) => {
              const r = byType.get(meta.type);
              return (
                <tr key={meta.type} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{meta.label}</td>
                  <td className="px-4 py-3">{r ? (r.is_enabled ? 'On' : 'Off') : '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r?.severity ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {r ? JSON.stringify(r.thresholds) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="btn-secondary text-xs"
                      onClick={() =>
                        setDraft(
                          r ?? {
                            alert_type: meta.type,
                            is_enabled: true,
                            severity: 'WARNING',
                            thresholds: {},
                            notify_channels: {},
                          },
                        )
                      }
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {draft && (
        <EditModal
          rule={draft}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSave={() => save.mutate(draft)}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

function EditModal({
  rule,
  onChange,
  onClose,
  onSave,
  saving,
}: {
  rule: AlertRule;
  onChange: (r: AlertRule) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const [thresholdsText, setThresholdsText] = useState(JSON.stringify(rule.thresholds, null, 2));
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div className="card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{rule.alert_type}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            ×
          </button>
        </header>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rule.is_enabled}
            onChange={(e) => onChange({ ...rule, is_enabled: e.target.checked })}
          />
          Enabled
        </label>
        <label className="mb-3 block text-sm">
          Severity
          <select
            value={rule.severity}
            onChange={(e) =>
              onChange({
                ...rule,
                severity: e.target.value as 'INFO' | 'WARNING' | 'CRITICAL',
              })
            }
            className="input mt-1 w-full"
          >
            <option value="INFO">INFO</option>
            <option value="WARNING">WARNING</option>
            <option value="CRITICAL">CRITICAL</option>
          </select>
        </label>
        <label className="block text-sm">
          Thresholds (JSON)
          <textarea
            rows={6}
            value={thresholdsText}
            onChange={(e) => {
              setThresholdsText(e.target.value);
              try {
                const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : {};
                onChange({ ...rule, thresholds: parsed });
              } catch {
                // hold edits until JSON is valid
              }
            }}
            className="input mt-1 w-full font-mono text-xs"
          />
        </label>
        <footer className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
